import { gql } from '@apollo/client';

/**
 * Placing a plan's trades — the three calls, in the order they happen.
 *
 * THE ONLY PATH IN THIS APP THAT MOVES A CLIENT'S MONEY, so what each call
 * does and does not do is worth being exact about:
 *
 *   1. `rebalanceTradesPreflight` runs every check the execute call runs —
 *      each sell fits inside the goal account's holding and has a folio, each
 *      buy clears its scheme minimum and amount grid — and says whether UPI
 *      may pay. It persists nothing and sends no OTP, so it is safe to run as
 *      the screen opens and again whenever the legs change.
 *   2. `executeRebalanceTrades` STAGES the basket and SENDS ONE OTP. Nothing
 *      is placed. It answers with the order id and session to confirm against.
 *   3. `confirmOrder` is what actually places it.
 *
 * Only MANDATE and UPI are accepted for a rebalance; the resolver refuses the
 * rest outright. A plan with sells can only be a mandate, because the buys are
 * funded by sale proceeds that land days later.
 */

/** Every refusal the execute call would give, before an OTP is sent. */
export const REBALANCE_TRADES_PREFLIGHT = gql`
  mutation RebalanceTradesPreflight($trades: [RebalanceTradeInput!]!, $goalId: Int) {
    rebalanceTradesPreflight(trades: $trades, goalId: $goalId) {
      success
      message
      errors
      upiAllowed
    }
  }
`;

/**
 * Stage the basket and send the OTP. `mandateId` is required the moment the
 * plan holds a buy: those buys debit that mandate days later with nobody
 * watching, so the instrument is proven before the OTP goes out.
 */
export const EXECUTE_REBALANCE_TRADES = gql`
  mutation ExecuteRebalanceTrades(
    $trades: [RebalanceTradeInput!]!
    $goalId: Int
    $mandateId: Int
    $paymentMode: OrderPaymentMode
  ) {
    executeRebalanceTrades(
      trades: $trades
      goalId: $goalId
      mandateId: $mandateId
      paymentMode: $paymentMode
    ) {
      success
      message
      orderId
      sessionId
    }
  }
`;

/** The OTP, and with it the actual placement. */
export const CONFIRM_ORDER = gql`
  mutation ConfirmOrder($input: ConfirmOrderInput!) {
    confirmOrder(input: $input) {
      success
      message
      orderStatus
      paymentStatus
      paymentMode
      mfOrderIds
      batchRef
      nseMessage
      authorizationPending
    }
  }
`;

/**
 * Trade ideas for a list of stocks, sized for a client's trading account: the
 * account's mandate sets the ceilings (loss per trade, position size, open
 * risk). Each trade carries its sizing and `risk_options` — the same trade at
 * a lower, medium and higher risk, every one within the account's limits.
 * Writes nothing; the run is published under `urlId`.
 */
export const GENERATE_STOCK_TRADES = gql`
  mutation GenerateStockTrades($input: StockTradeGenerateInput!) {
    generateStockTrades(input: $input) {
      success
      status
      message
      urlId
      asOf
      horizon
      trades
      skipped
      book
      notes
    }
  }
`;

/**
 * Set up a client's trading account and the mandate it runs under. The backend
 * checks the mandate for its type — the fields that must be stated, limits that
 * hold together — and refuses with every problem in its message.
 */
export const CREATE_TRADING_GOAL = gql`
  mutation CreateTradingGoal($input: TradingGoalCreate!) {
    createTradingGoal(input: $input) {
      userinvestmentgoalId
      goalType
      capital
    }
  }
`;

/**
 * Change a trading account's rules — only the fields sent change. The rules are
 * the client's own; the backend checks the merged mandate still holds and
 * refuses with its reasons when it does not.
 */
export const UPDATE_TRADING_GOAL = gql`
  mutation UpdateTradingGoal($input: TradingGoalUpdate!) {
    updateTradingGoal(input: $input) {
      userinvestmentgoalId
      goalType
      mandateVersion
    }
  }
`;
